import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CampaignSelector = ({ 
  campaigns = [], 
  selectedCampaign, 
  onCampaignSelect, 
  loading = false, 
  onRefresh 
}) => {
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');

  const formatCampaignOptions = () => {
    const options = [
      { value: '', label: 'No Campaign (Individual QR codes)' }
    ];
    
    if (campaigns?.length > 0) {
      campaigns?.forEach(campaign => {
        options?.push({
          value: campaign?.id,
          label: `${campaign?.name} (${campaign?.status || 'active'})`
        });
      });
    }
    
    return options;
  };

  const handleCampaignChange = (campaignId) => {
    if (campaignId === '') {
      onCampaignSelect(null);
    } else {
      const campaign = campaigns?.find(c => c?.id === campaignId);
      onCampaignSelect(campaign || null);
    }
  };

  const handleCreateNewCampaign = () => {
    // This would typically create a new campaign via API
    // For now, we'll just show a placeholder
    setShowCreateNew(false);
    setNewCampaignName('');
    // You could implement campaign creation here
    alert('Campaign creation feature would be implemented here');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Campaign Assignment</h3>
        <Button
          onClick={onRefresh}
          variant="ghost"
          size="sm"
          disabled={loading}
        >
          <Icon 
            name="RefreshCw" 
            size={16} 
            className={loading ? 'animate-spin' : ''} 
          />
        </Button>
      </div>
      <div className="space-y-3">
        {/* Campaign Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Campaign
          </label>
          {loading ? (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 animate-pulse">
              <div className="h-4 bg-gray-300 rounded"></div>
            </div>
          ) : (
            <Select
              value={selectedCampaign?.id || ''}
              onChange={handleCampaignChange}
              options={formatCampaignOptions()}
              placeholder="Select a campaign..."
            />
          )}
          <p className="text-xs text-gray-500 mt-1">
            Assign all imported QR codes to this campaign by default
          </p>
        </div>

        {/* Create New Campaign */}
        <div className="border-t border-gray-200 pt-3">
          {showCreateNew ? (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Campaign Name
                </label>
                <input
                  type="text"
                  value={newCampaignName}
                  onChange={(e) => setNewCampaignName(e?.target?.value)}
                  placeholder="Enter campaign name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  onClick={() => {
                    setShowCreateNew(false);
                    setNewCampaignName('');
                  }}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateNewCampaign}
                  size="sm"
                  disabled={!newCampaignName?.trim()}
                >
                  Create Campaign
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => setShowCreateNew(true)}
              variant="outline"
              size="sm"
              className="w-full justify-center"
            >
              <Icon name="Plus" size={16} className="mr-2" />
              Create New Campaign
            </Button>
          )}
        </div>

        {/* Selected Campaign Info */}
        {selectedCampaign && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="Target" size={16} className="text-purple-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-purple-800">
                  {selectedCampaign?.name}
                </h4>
                {selectedCampaign?.description && (
                  <p className="text-xs text-purple-700 mt-1">
                    {selectedCampaign?.description}
                  </p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-xs text-purple-600">
                  <span>Status: {selectedCampaign?.status || 'Active'}</span>
                  {selectedCampaign?.target_scans && (
                    <span>Target: {selectedCampaign?.target_scans} scans</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Campaign Stats */}
        {campaigns?.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Available Campaigns</h4>
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
              <div>
                <span className="font-medium">Total Campaigns:</span> {campaigns?.length}
              </div>
              <div>
                <span className="font-medium">Active:</span> {campaigns?.filter(c => c?.status === 'active')?.length || campaigns?.length}
              </div>
            </div>
          </div>
        )}

        {/* No Campaigns Message */}
        {!loading && campaigns?.length === 0 && (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Icon name="Target" size={24} className="text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No campaigns found</p>
            <p className="text-xs text-gray-500 mt-1">
              Create a campaign to organize your imported QR codes
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignSelector;